// Fire-and-forget visit ping to a Discord webhook.
//
// Sends ONE embed per session, on page exit, so it can include engagement
// (time on page, scroll depth, deepest section). Gated behind bot filtering so
// only genuinely engaged, human-looking visitors are reported.
//
// NOTE: A visitor's name is NOT obtainable from the browser — this is anonymous
// metadata only. Bot filtering here is best-effort, not foolproof.

const WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL;
const ACCENT_COLOR = 0xff385c;
const AFK_TIMEOUT_MS = 60 * 60 * 1000; // Ping after this much inactivity (1 hour).
const AFK_CHECK_INTERVAL_MS = 60 * 1000; // How often to check for idleness.

const BOT_UA_RE =
  /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|whatsapp|telegram|discordbot|slackbot|headless|lighthouse|pagespeed|gtmetrix|pingdom|prerender|python-requests|axios|curl|wget|node-fetch|puppeteer|playwright|phantom|scrapy|semrush|ahrefs|dotbot|mj12/i;

let initialized = false;

function isLikelyBot() {
  const ua = navigator.userAgent || "";
  if (!ua) return true; // No UA at all is a strong bot signal.
  if (navigator.webdriver) return true; // Selenium/Puppeteer/Playwright automation.
  if (BOT_UA_RE.test(ua)) return true;
  return false;
}

function detectBrowser(ua) {
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\/|Opera/.test(ua)) return "Opera";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return "Unknown";
}

function detectOS(ua) {
  if (/Windows NT/.test(ua)) return "Windows";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown";
}

function detectDeviceType(ua) {
  if (/iPad|Tablet/.test(ua)) return "Tablet";
  if (/Mobi|Android|iPhone/.test(ua)) return "Mobile";
  return "Desktop";
}

function formatDuration(ms) {
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s}s`;
}

async function lookupLocation() {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch("https://ipwho.is/", { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const data = await res.json();
    if (data && data.success === false) return null;
    return data;
  } catch {
    return null;
  }
}

function computeDeepestSection(maxScrollY) {
  const sections = [
    { id: "skills", label: "Skills" },
    { id: "resume", label: "Experience" },
    { id: "contact", label: "Contact" },
  ];
  let deepest = "Hero";
  const reachLine = maxScrollY + window.innerHeight;
  for (const s of sections) {
    const el = document.getElementById(s.id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (top <= reachLine) deepest = s.label;
    }
  }
  return deepest;
}

// Immediately posts a clearly-labeled test ping, bypassing all gating.
// Used by the dev-only test button. Returns a small result object.
export async function sendTestPing() {
  if (typeof window === "undefined") return { ok: false, reason: "no-window" };
  if (!WEBHOOK_URL) return { ok: false, reason: "no-webhook" };

  const ua = navigator.userAgent || "";
  const geo = await lookupLocation();
  const location = geo
    ? [geo.city, geo.region, geo.country].filter(Boolean).join(", ") || "Unknown"
    : "Unknown";

  const payload = {
    username: "Portfolio Visitor (test)",
    embeds: [
      {
        title: "🧪 Test ping",
        description: "Manual test from the dev button — not a real visit.",
        color: ACCENT_COLOR,
        url: window.location.href,
        fields: [
          { name: "IP", value: geo?.ip || "Unknown", inline: true },
          { name: "Location", value: location, inline: true },
          { name: "Browser", value: detectBrowser(ua), inline: true },
          { name: "OS", value: detectOS(ua), inline: true },
          { name: "Device", value: detectDeviceType(ua), inline: true },
          { name: "Referrer", value: document.referrer || "Direct", inline: true },
          { name: "ISP", value: geo?.connection?.isp || geo?.connection?.org || "Unknown", inline: true },
        ],
        timestamp: new Date().toISOString(),
      },
    ],
  };

  try {
    const res = await fetch(WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return { ok: res.ok, status: res.status };
  } catch {
    return { ok: false, reason: "network" };
  }
}

export default function trackVisit() {
  if (typeof window === "undefined") return;
  if (!WEBHOOK_URL) return; // No webhook configured — silently no-op.
  if (initialized) return; // Guard against React StrictMode double-invoke.
  initialized = true;

  // Skip prerender/prefetch loads (some browsers pre-render links in background).
  if (document.visibilityState === "prerender") return;
  if (isLikelyBot()) return;

  const startTime = Date.now();
  let interacted = false;
  let maxScrollY = window.scrollY || 0;
  let maxScrollPct = 0;
  let geo = null;
  let sent = false;

  lookupLocation().then((result) => {
    geo = result;
  });

  let lastActivity = Date.now();

  // Any real input counts as activity: flags the visitor as human and resets
  // the AFK timer.
  const bumpActivity = () => {
    interacted = true;
    lastActivity = Date.now();
  };

  const onScroll = () => {
    bumpActivity();
    maxScrollY = Math.max(maxScrollY, window.scrollY);
    const docHeight = document.documentElement.scrollHeight || 1;
    const pct = Math.min(
      100,
      Math.round(((window.scrollY + window.innerHeight) / docHeight) * 100)
    );
    maxScrollPct = Math.max(maxScrollPct, pct);
  };

  const activityEvents = ["pointermove", "keydown", "touchstart", "click", "wheel"];
  activityEvents.forEach((ev) =>
    window.addEventListener(ev, bumpActivity, { passive: true })
  );
  window.addEventListener("scroll", onScroll, { passive: true });

  // Best-effort refresh detection. The browser can't tell a reload from a real
  // tab-close at unload time, but a keyboard reload (F5 / Ctrl+R / Cmd+R) is
  // detectable just before it happens, so we flag it and skip the ping. This
  // won't catch the reload button or mobile pull-to-refresh — those are
  // indistinguishable from closing the tab.
  let reloading = false;
  const onReloadKey = (e) => {
    const isReloadKey =
      e.key === "F5" ||
      ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R"));
    if (isReloadKey) {
      reloading = true;
      // If the reload somehow doesn't happen, don't permanently mute the ping.
      window.setTimeout(() => {
        reloading = false;
      }, 1500);
    }
  };
  window.addEventListener("keydown", onReloadKey);

  const send = (reason) => {
    if (sent) return;

    // Only report genuine, human-looking visits (bots were already filtered on
    // setup; here we require at least one real interaction).
    if (!interacted) return;

    // A keyboard-initiated refresh isn't a real exit — skip it without marking
    // the session as pinged, so a later genuine close still reports.
    if (reloading) return;

    // Ping each browser at most once, ever. Persisted in localStorage so it
    // survives reloads, new tabs, and future visits — the same person won't be
    // reported again until they clear site data (or use another browser/device).
    // Set before sending because the exit ping is fire-and-forget; the dev test
    // button stays available for re-testing regardless.
    try {
      if (localStorage.getItem("visitPinged")) {
        sent = true;
        return;
      }
      localStorage.setItem("visitPinged", String(Date.now()));
    } catch {
      // localStorage unavailable (e.g. privacy mode) — proceed without dedupe.
    }

    const dwellMs = Date.now() - startTime;

    sent = true;
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("keydown", onReloadKey);
    activityEvents.forEach((ev) => window.removeEventListener(ev, bumpActivity));
    clearInterval(afkInterval);

    const ua = navigator.userAgent || "";
    const location = geo
      ? [geo.city, geo.region, geo.country].filter(Boolean).join(", ") || "Unknown"
      : "Unknown";

    const payload = {
      username: "Portfolio Visitor",
      embeds: [
        {
          title: "👀 New visit to your portfolio",
          color: ACCENT_COLOR,
          url: window.location.href,
          fields: [
            { name: "Trigger", value: reason || "Tab closed", inline: true },
            { name: "IP", value: geo?.ip || "Unknown", inline: true },
            { name: "Location", value: location, inline: true },
            { name: "Browser", value: detectBrowser(ua), inline: true },
            { name: "OS", value: detectOS(ua), inline: true },
            { name: "Device", value: detectDeviceType(ua), inline: true },
            { name: "Time on page", value: formatDuration(dwellMs), inline: true },
            { name: "Scroll depth", value: `${maxScrollPct}%`, inline: true },
            { name: "Deepest section", value: computeDeepestSection(maxScrollY), inline: true },
            { name: "Referrer", value: document.referrer || "Direct", inline: true },
            { name: "ISP", value: geo?.connection?.isp || geo?.connection?.org || "Unknown", inline: true },
          ],
          timestamp: new Date().toISOString(),
        },
      ],
    };

    const body = JSON.stringify(payload);
    try {
      // Use fetch(keepalive) rather than navigator.sendBeacon: Discord's webhook
      // accepts an application/json fetch (proven by the dev test button), but a
      // sendBeacon JSON Blob fails Discord's CORS preflight silently. keepalive
      // lets the request survive the page being backgrounded/closed.
      fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Never let tracking affect the user experience.
    }
  };

  // Ping when the visitor has been idle (AFK) for over an hour.
  const afkInterval = setInterval(() => {
    if (Date.now() - lastActivity >= AFK_TIMEOUT_MS) {
      send("Idle 1h+");
    }
  }, AFK_CHECK_INTERVAL_MS);

  // Ping when the visitor closes the tab / navigates away.
  window.addEventListener("pagehide", () => send("Tab closed"));
}
